// Tiny body-composition helpers. Pure functions of stored units (kg, cm) so they're easy to
// test and unit-agnostic — display code converts elsewhere.

/** Body Mass Index from weight (kg) + height (cm). null if either is missing/invalid. */
export function bmi(weightKg, heightCm) {
  const w = Number(weightKg), h = Number(heightCm);
  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
  const m = h / 100;
  return Math.round((w / (m * m)) * 10) / 10;
}

// WHO adult BMI bands. `tone` maps to our status colours (success/warn/danger).
const BANDS = [
  { max: 18.5, label: "Underweight", tone: "warn" },
  { max: 25, label: "Healthy", tone: "success" },
  { max: 30, label: "Overweight", tone: "warn" },
  { max: Infinity, label: "Obese", tone: "danger" },
];

/** { label, tone } for a BMI value, or null. BMI is a rough guide and ignores muscle mass. */
export function bmiCategory(value) {
  if (value == null || !isFinite(value)) return null;
  return BANDS.find((b) => value < b.max) || BANDS[BANDS.length - 1];
}

// ── Age & heart-rate zones ────────────────────────────────────────────────────
// We store birth_year (not a static age, which goes stale); derive current age on demand.
// nowYear is injectable for deterministic tests.
export function ageFromBirthYear(birthYear, nowYear = new Date().getFullYear()) {
  const y = Number(birthYear);
  if (!Number.isInteger(y) || y < 1900 || y > nowYear) return null;
  const age = nowYear - y;
  return age >= 0 && age <= 130 ? age : null;
}

/**
 * Estimated maximum heart rate (bpm) from age, via the Tanaka–Monahan–Seals formula
 * (208 − 0.7·age). Less age-biased than the old Fox 220−age (which overestimates the young
 * and underestimates the old); SD ~±10 bpm, so treat zones as guides, not gospel. null if age
 * is missing/implausible. Source: Tanaka et al. 2001, J Am Coll Cardiol.
 */
export function maxHeartRate(age) {
  const a = Number(age);
  if (!isFinite(a) || a < 5 || a > 130) return null;
  return Math.round(208 - 0.7 * a);
}

/**
 * Zone 2 ("conversational"/easy aerobic) target heart-rate band, ~60–70% of max HR — the base
 * for the cardio coaching tip. Returns { low, high } bpm, or null. The exact % bounds may be
 * refined when the age-stratified evidence pass lands.
 */
export function heartRateZone2(age) {
  const hrMax = maxHeartRate(age);
  if (hrMax == null) return null;
  return { low: Math.round(hrMax * 0.6), high: Math.round(hrMax * 0.7), hrMax };
}

/**
 * US Navy Body Fat % from height, waist, neck (and hips for females).
 * Measurements in cm. Returns estimated body fat percentage rounded to 1 decimal place, or null.
 */
export function navyBodyFat(params) {
  if (!params || typeof params !== "object") return null;
  const { gender = "male", heightCm, waistCm, neckCm, hipsCm } = params;
  const h = Number(heightCm);
  const w = Number(waistCm);
  const n = Number(neckCm);
  if (!isFinite(h) || !isFinite(w) || !isFinite(n) || h <= 0 || w <= 0 || n <= 0) return null;

  const isFemale = String(gender).toLowerCase() === "female";
  if (isFemale) {
    const hips = Number(hipsCm);
    if (!isFinite(hips) || hips <= 0) return null;
    const diff = w + hips - n;
    if (diff <= 0) return null;
    const fat = 495 / (1.29579 - 0.35004 * Math.log10(diff) + 0.22100 * Math.log10(h)) - 450;
    return isFinite(fat) ? Math.max(3, Math.min(60, Math.round(fat * 10) / 10)) : null;
  } else {
    const diff = w - n;
    if (diff <= 0) return null;
    const fat = 495 / (1.0324 - 0.19077 * Math.log10(diff) + 0.15456 * Math.log10(h)) - 450;
    return isFinite(fat) ? Math.max(3, Math.min(60, Math.round(fat * 10) / 10)) : null;
  }
}

/**
 * Fat-Free Mass Index (FFMI) from weight (kg), height (cm), and body fat (%).
 * Returns { raw, normalized, leanMassKg } or null if inputs are invalid.
 * Normalized FFMI scales to 1.8m height baseline (Kouri et al. 1995).
 */
export function ffmi(params) {
  if (!params || typeof params !== "object") return null;
  const { weightKg, heightCm, bodyFatPct } = params;
  const w = Number(weightKg);
  const h = Number(heightCm);
  const bf = Number(bodyFatPct);
  if (!isFinite(w) || !isFinite(h) || !isFinite(bf) || w <= 0 || h <= 0 || bf < 0 || bf >= 100) return null;

  const hM = h / 100;
  const leanMassKg = w * (1 - bf / 100);
  const raw = leanMassKg / (hM * hM);
  const normalized = raw + 6.1 * (1.8 - hM);

  return {
    raw: Math.round(raw * 10) / 10,
    normalized: Math.round(normalized * 10) / 10,
    leanMassKg: Math.round(leanMassKg * 10) / 10,
  };
}
